import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { User, PaginationParams } from '@console/utils';
import { usersApi, CreateUserDto, UpdateUserDto } from '@/lib/api/users';
import { toast } from 'sonner';

export function useUsers(defaultParams?: PaginationParams) {
  const { t } = useTranslation();
  const [users, setUsers] = useState<User[]>([]);
  const [pagination, setPagination] = useState({
    total: 0,
    page: defaultParams?.page || 1,
    limit: defaultParams?.limit || 10,
    totalPages: 0,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const fetchUsers = useCallback(
    async (params?: PaginationParams) => {
      try {
        setIsLoading(true);
        const response = await usersApi.getAll(params);
        setUsers(response.data);
        setPagination({
          total: response.total,
          page: response.page,
          limit: response.limit,
          totalPages: response.totalPages,
        });
      } catch (err: any) {
        toast.error(err.response?.data?.message || t('users.loadError'));
      } finally {
        setIsLoading(false);
      }
    },
    [t]
  );

  const createUser = useCallback(
    async (userData: CreateUserDto) => {
      try {
        setIsSaving(true);
        const newUser = await usersApi.create(userData);
        // Add the new user to the beginning of the current list
        setUsers((prev) => [newUser, ...prev]);
        // Update pagination total
        setPagination((prev) => ({ ...prev, total: prev.total + 1 }));
        toast.success(t('users.createSuccess'));
        return true;
      } catch (err: any) {
        toast.error(err.response?.data?.message || t('users.createError'));
        return false;
      } finally {
        setIsSaving(false);
      }
    },
    [t]
  );

  const updateUser = useCallback(
    async (id: string, userData: UpdateUserDto) => {
      try {
        setIsSaving(true);
        const updatedUser = await usersApi.update(id, userData);
        // Update the user in the current list
        setUsers((prev) => prev.map((user) => (user.id === id ? updatedUser : user)));
        toast.success(t('users.updateSuccess'));
        return true;
      } catch (err: any) {
        toast.error(err.response?.data?.message || t('users.updateError'));
        return false;
      } finally {
        setIsSaving(false);
      }
    },
    [t]
  );

  const deleteUser = useCallback(
    async (id: string) => {
      try {
        setIsSaving(true);
        await usersApi.delete(id);
        // Remove the user from the current list
        setUsers((prev) => prev.filter((user) => user.id !== id));
        // Update pagination total
        setPagination((prev) => ({ ...prev, total: prev.total - 1 }));
        toast.success(t('users.deleteSuccess'));
        return true;
      } catch (err: any) {
        toast.error(err.response?.data?.message || t('users.deleteError'));
        return false;
      } finally {
        setIsSaving(false);
      }
    },
    [t]
  );

  useEffect(() => {
    fetchUsers({
      page: defaultParams?.page || 1,
      limit: defaultParams?.limit || 10,
    });
  }, [fetchUsers, defaultParams?.page, defaultParams?.limit]);

  return {
    users,
    pagination,
    isLoading,
    isSaving,
    createUser,
    updateUser,
    deleteUser,
    refetch: fetchUsers,
  };
}
